/**
 * PlatformWebHost — the narrow, typed slice of core web an extension's web code consumes at runtime
 * (core/ext W2-f0); `PlatformExtensionWeb` is what it declares. No core HTTP client, auth store or JWT:
 * a host request rejects only with PlatformWebHostRequestError, never the transport's error object.
 * Type-only React import: the roster and its assert load without React.
 */
// `satisfies Record<…>` makes the roster total: a capability added to the type and not listed here fails tsc.
const CAPABILITY_KIND = {
    executeTool: 'function',
    getEntityDefinition: 'function',
    'pipeline.list': 'function',
    'pipeline.getRun': 'function',
    'pipeline.startRun': 'function',
    'components.EntityList': 'component',
};
export const PLATFORM_WEB_HOST_CAPABILITIES = Object.freeze(Object.keys(CAPABILITY_KIND));
/** usePlatformWebHost() ran outside a PlatformWebHostProvider. */
export class PlatformWebHostMissingError extends Error {
    constructor() {
        super('usePlatformWebHost() was called outside a PlatformWebHostProvider. Core mounts one at the App root; ' +
            'a test wraps the tree in one with createTestPlatformWebHost() (@elmes/plugin-sdk/web/testing).');
        this.name = 'PlatformWebHostMissingError';
    }
}
/**
 * The rejection of every host request (executeTool, getEntityDefinition, pipeline.*). Carries no
 * request config, headers or raw response; `message` is the transport error's own.
 */
export class PlatformWebHostRequestError extends Error {
    /** HTTP status, when the server answered. */
    status;
    /** The server's machine-readable error `code`, when its body carried one. */
    code;
    constructor(message, detail = {}) {
        super(message);
        this.name = 'PlatformWebHostRequestError';
        if (detail.status !== undefined)
            this.status = detail.status;
        if (detail.code !== undefined)
            this.code = detail.code;
    }
}
/** A host value lacks one or more roster capabilities. */
export class PlatformWebHostShapeError extends Error {
    missing;
    constructor(missing) {
        super(`PlatformWebHost is missing ${missing.length} capabilit${missing.length === 1 ? 'y' : 'ies'}: ` +
            missing.map((cap) => `${cap} (${CAPABILITY_KIND[cap]})`).join(', '));
        this.name = 'PlatformWebHostShapeError';
        this.missing = missing;
    }
}
function readPath(value, path) {
    let current = value;
    for (const key of path.split('.')) {
        if ((typeof current !== 'object' && typeof current !== 'function') || current === null)
            return undefined;
        current = current[key];
    }
    return current;
}
// memo/forwardRef/lazy components are objects tagged with one of these; an element ($$typeof too) is not.
const EXOTIC_COMPONENT_TAGS = new Set([
    Symbol.for('react.memo'),
    Symbol.for('react.forward_ref'),
    Symbol.for('react.lazy'),
]);
function isComponent(value) {
    if (typeof value === 'function')
        return true;
    return typeof value === 'object' && value !== null && EXOTIC_COMPONENT_TAGS.has(value.$$typeof);
}
/** Validates every capability before any is read; lists all missing ones in a single throw. */
export function assertPlatformWebHost(value) {
    const missing = PLATFORM_WEB_HOST_CAPABILITIES.filter((cap) => {
        const leaf = readPath(value, cap);
        return CAPABILITY_KIND[cap] === 'component' ? !isComponent(leaf) : typeof leaf !== 'function';
    });
    if (missing.length > 0)
        throw new PlatformWebHostShapeError(missing);
}
//# sourceMappingURL=host.js.map